import { Card, Divider, mergeClasses } from "@fluentui/react-components";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { DocumentReader } from "../../components/DocumentReader";
import { useAppData } from "../../hooks";
import { IClause, IDocument } from "../../interfaces";
import { workspaceApi } from "../../services/workspaces";
import { SuggestionSearchMode } from "../../types/enums";
import { SuggestionList } from "./components/SuggestionList";
import { SuggestionToolbar } from "./components/SuggestionToolbar";
import { useStyles } from "./styles";

export type SuggestionResultProps = {
  appId: string;
};

export const SuggestionResult = memo(({ appId }: SuggestionResultProps) => {
  const styles = useStyles();
  const [searchText] = useAppData<string>(appId, "searchText");
  const [searchType] = useAppData<SuggestionSearchMode>(appId, "searchType");
  const [isFilterOpened] = useAppData<boolean>(appId, "isFilterOpened");
  const [isRankingOpened] = useAppData<boolean>(appId, "isRankingOpened");

  const [openDocumentViewer, setOpenDocumentViewer] = useState<boolean>(false);
  const documentViewerRef = useRef<{ clause?: IClause; document: IDocument } | undefined>(undefined);

  const documentViewerButtonRef = useRef<HTMLButtonElement>(null);
  const mountNodeRef = useRef<HTMLDivElement>(null);

  const onOpenClauseDocument = useCallback((document: IDocument, clause?: IClause) => {
    documentViewerRef.current = { document, clause };
    setOpenDocumentViewer(true);
    documentViewerButtonRef.current?.click();
  }, []);

  const { data: workspaces = [] } = workspaceApi.useGetWorkspacesQuery();

  const workspaceDict = useMemo(() => {
    const dict: Record<string, string> = {};

    workspaces.forEach((w) => {
      dict[w.workspaceId] = w.name;
    });

    return dict;
  }, [workspaces]);

  const [workspaceId] = useAppData<string>(appId, "workspaceId");

  // Ref to access SuggestionList imperative API
  const suggestionListRef = useRef<{ exportToExcel: () => void } | null>(null);
  const handleExport = useCallback(() => suggestionListRef.current?.exportToExcel(), []);

  return (
    <div className={styles.container} ref={mountNodeRef} key={appId}>
      <div
        className={mergeClasses(
          styles.overlay,
          !isFilterOpened && !isRankingOpened && !openDocumentViewer ? styles.displayNone : ""
        )}
      ></div>
      <DocumentReader
        mountNode={mountNodeRef.current}
        document={documentViewerRef.current?.document!}
        textBlock={documentViewerRef.current?.clause?.clause_text}
        trigger={
          <button className={styles.disabledTrigger} ref={documentViewerButtonRef}>
            "disabledTrigger"
          </button>
        }
        onClose={() => setOpenDocumentViewer(false)}
      />
      <SuggestionToolbar appId={appId} onExport={handleExport} />
      <div className={styles.searchTextPlaceholderContainer}>
        <div className={styles.dividerContainer}>
          <Divider>
            <span>
              {searchType === SuggestionSearchMode.KEYWORD_SEARCH ? "Boolean Search (Beta) " : "Smart Search"} in{" "}
              {workspaceDict[workspaceId]}
            </span>
          </Divider>
        </div>
        <div className={styles.cardContainer}>
          <Card className={styles.card}>
            <div className={styles.textContainer}>{searchText}</div>
          </Card>
        </div>
      </div>

      <div className={styles.suggestionListContainer}>
        <div className={styles.dividerContainer}>
          <Divider>
            <span className={styles.dividerText}>Results</span>
          </Divider>
        </div>
        <SuggestionList ref={suggestionListRef} appId={appId} onOpenDocument={onOpenClauseDocument} />
      </div>
    </div>
  );
});
